import { useEffect, useState } from 'react';

/**
 * Selector for any modal surface that should take priority over the floating
 * launchers (Quick Actions "+", AI Co-Pilot, Page Help). Radix dialogs render
 * `role="dialog"`; the explicit data selector covers wrappers that omit it.
 */
export const OVERLAY_SELECTOR =
  '[role="dialog"], [role="alertdialog"], [data-state="open"][data-radix-dialog-content]';

const DEBOUNCE_MS = 60;

function hasOpenOverlay(root: Document): boolean {
  return root.querySelector(OVERLAY_SELECTOR) !== null;
}

/**
 * PT-AUDIT-0147 / 0149 / 0171: returns true while any dialog is present in the
 * document, so the floating launchers can step out of the way of dialog footer
 * buttons (Create Customer, Create anyway, ...).
 *
 * Implemented with a debounced MutationObserver on document.body; cleaned up on
 * unmount.
 */
export function useOverlayOpen(): boolean {
  const [open, setOpen] = useState<boolean>(() =>
    typeof document === 'undefined' ? false : hasOpenOverlay(document),
  );

  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return undefined;

    let timer: number | undefined;
    const update = () => {
      timer = undefined;
      setOpen(hasOpenOverlay(document));
    };
    const schedule = () => {
      if (timer !== undefined) window.clearTimeout(timer);
      timer = window.setTimeout(update, DEBOUNCE_MS);
    };

    update();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['role', 'data-state', 'data-radix-dialog-content'],
    });

    return () => {
      observer.disconnect();
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, []);

  return open;
}
